import { useViewer } from "@/authentication/ViewerProvider"
import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { rootPath, updatePasswordPath } from "@/novus/novus-paths"
import { displayValidationErrors } from "@/util/validations"
import { useMutation } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { z, ZodType } from "zod"

const UpdateViewerMutation = gql(`
  mutation UpdateViewer($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        ...Viewer
      }
      errors {
        path
        fullMessages
      }
    }
  }
`)

type FormValues = {
  email: string
  firstName: string
  lastName: string
  password: string
}

const schema: ZodType<FormValues> = z.object({
  email: z
    .string()
    .min(1, "Email address is required")
    .email({ message: "Email address is invalid" }),
  firstName: z.string(),
  lastName: z.string(),
  password: z.string(),
})

const EditProfileForm = () => {
  const { viewer } = useViewer()
  const navigate = useNavigate()
  const [mutate] = useMutation(UpdateViewerMutation)
  const { toast } = useToast()

  const defaultValues: FormValues = {
    email: viewer.email || "",
    firstName: viewer.firstName || "",
    lastName: viewer.lastName || "",
    password: "",
  }

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState, setError } = methods

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      const { data } = await mutate({
        variables: {
          input: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
          },
        },
      })

      if (data?.updateUser.errors && data?.updateUser.errors.length > 0) {
        throw data?.updateUser.errors
      }

      toast({
        title: "Profile has been updated.",
      })

      navigate(rootPath.pattern)
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "user",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Profile was not updated.",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup row={true}>
          <RhfField
            name="firstName"
            control={control}
            placeholder="First name"
            label="First Name"
            autoFocus={true}
          />
          <RhfField
            name="lastName"
            control={control}
            placeholder="Last name"
            label="Last Name"
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="email"
            control={control}
            placeholder="Email address"
            label="Email Address"
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="password"
            control={control}
            type="password"
            placeholder="••••••••••••••••••••"
            label="Password"
            rightLabelContent={
              <Link
                to={updatePasswordPath.pattern}
                className="text-primary text-sm"
              >
                Update Password
              </Link>
            }
            disabled={true}
          />
        </FieldGroup>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Save & Update
          </Button>
          <Link to={rootPath.pattern} className="text-neutral-600 text-sm">
            Close & Cancel
          </Link>
        </FormActions>
      </form>
    </FormProvider>
  )
}

export default EditProfileForm
